import * as React from 'react';

import { AuthProvider } from '@40seas/entities/auth';
import { IntentType, SignupIntent } from '@40seas/entities/intents';
import { useExecute, UserContext } from '@40seas/frontend/libs/hooks';
import { SelfManagedAuthService } from '@40seas/frontend/libs/services';
import { CentralizedAnimatedLoader, PaymentThemeProvider, LoginContextProvider, LoginContext } from '@40seas/payments-shared';
import { QueryClient, QueryClientProvider } from 'react-query';
import { IntentsEmbed } from '../components/common/modal/intents/IntentsEmbed';
import { initIntent } from '../components/common/modal/intents/IntentsModal';
import { useAuth } from '../hooks/useAuth';
import { useAuth as useAuthV2 } from '@40seas/auth-client';
import { IntentParams } from '../components/common/modal/intents/types';
import { useCurrentUserNew } from '../hooks/useCurrentUserNew';
import useInitDataProvider from '../hooks/useInitDataProvider';
import DefaultDataProvider from './defaultDataProvider';
import { getAuthProvider } from '@40seas/auth-client';

const getTenantId = (user, tenantId): string | undefined => {
    return (
        user?.organizations.find((org) => org.tenants?.find((tenant) => tenant.tenantId === tenantId))?.tenants?.[0].tenantId ||
        user?.organizations?.[0]?.tenants?.[0]?.tenantId ||
        tenantId
    );
};

export const UserProviderWithAuthenticationNew = ({ children, token }: { children: React.ReactNode; token?: string }) => {
    const { user, fetchUser } = useCurrentUserNew();
    const dataProvider = useInitDataProvider() as DefaultDataProvider;

    const [intentData, setIntentData] = React.useState<IntentParams>();
    const [intentLoading, setIntentLoading] = React.useState(false);
    const { isAuthenticated, isLoading } = useAuth();
    const queryClient = new QueryClient();

    useExecute(
        async () => {
            setIntentLoading(true);
            const params = window.location.pathname.split('/');
            const [_, ...slug] = params;

            const tenantId = getTenantId(user, slug.join('/').replaceAll('/', '-').toUpperCase());
            const landingConfiguration = localStorage.getItem('guestLandingData');
            const data = await initIntent({
                dataProvider,
                intentType: IntentType.Signup,
                tenant: tenantId,
                initPayload: {
                    slug: tenantId,
                    ...(landingConfiguration ? { landingConfiguration: JSON.parse(landingConfiguration) } : {}),
                },
                existingToken: token,
                obtainNewToken: true,
            });

            setIntentData({
                intentType: IntentType.Signup,
                isLoading: false,
                isEmbedded: false,
                data,
                callbacks: {
                    onConfirm: async () => {
                        handleLoginLink();
                        await fetchUser();
                    },
                },
            });

            setIntentLoading(false);
        },
        () => {
            if (isLoading) return false;
            if (!isAuthenticated) return true;
            if (user && (!user.emailVerified || !user.organizations?.length)) return true;
            return false;
        },
    );

    useExecute(
        async () => {
            if (isAuthenticated) {
                await fetchUser();
            }
        },
        () => !isLoading,
    );

    if (isLoading || intentLoading) {
        return <CentralizedAnimatedLoader />;
    }

    if (isAuthenticated && user?.emailVerified && user?.organizations?.length > 0) {
        return <UserContext.Provider value={user!}>{children}</UserContext.Provider>;
    }

    const authProvider = getAuthProvider();
    const isSelfManagedAuth = authProvider === AuthProvider.SelfManaged;

    const signupContent = (
        <PaymentThemeProvider
            data={{ theme: intentData?.data?.intent['theme'] }}
            style={{ display: 'flex', width: '100%', height: '100vh' }}
        >
            {intentData && <IntentsEmbed theme={(intentData?.data?.intent as SignupIntent)?.theme} params={intentData} />}
        </PaymentThemeProvider>
    );

    return (
        <QueryClientProvider client={queryClient}>
            {isSelfManagedAuth ? (
                <SelfManagedLoginContextProvider>{signupContent}</SelfManagedLoginContextProvider>
            ) : (
                <LoginContextProvider popupOrigin="dashboard">{signupContent}</LoginContextProvider>
            )}
        </QueryClientProvider>
    );
};

/**
 * LoginContext provider for self-managed auth.
 * Provides login methods that use the self-managed auth service instead of Auth0.
 */
const SelfManagedLoginContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { loginWithGoogle, loginWithMicrosoft, resendVerification, verifyCode } = useAuthV2();

    const contextValue = React.useMemo(
        () => ({
            loginWithGoogle: async () => {
                try {
                    return await loginWithGoogle();
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            loginWithMicrosoft: async () => {
                try {
                    return await loginWithMicrosoft();
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            login: async (email: string, password: string) => {
                try {
                    const response = await SelfManagedAuthService.loginWithPassword(email, password);
                    return { accessToken: response.tokens.accessToken };
                } catch (error) {
                    // Extract error message string to match Auth0 iframe auth behavior
                    // The state machine stores event.data in errors.formSubmit which must be a string
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            signup: async (email: string, password: string) => {
                try {
                    // Self-managed auth requires email verification before login
                    await SelfManagedAuthService.register(email, password);
                    // Return result indicating email verification is required
                    return { requiresEmailVerification: true as const, email };
                } catch (error) {
                    // Extract error message string to match Auth0 iframe auth behavior
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            resendVerification: async (email: string) => {
                try {
                    await resendVerification(email);
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
            verifyCode: async (email: string, code: string) => {
                try {
                    const response = await verifyCode(email, code);
                    return { accessToken: response.tokens.accessToken };
                } catch (error) {
                    throw error instanceof Error ? error.message : String(error);
                }
            },
        }),
        [loginWithGoogle, loginWithMicrosoft, resendVerification, verifyCode],
    );

    return <LoginContext.Provider value={contextValue}>{children}</LoginContext.Provider>;
};

function handleLoginLink() {
    const url = new URL(window.location.href);

    if (url.pathname.endsWith('/login') || url.pathname.endsWith('signup')) {
        url.pathname = '';
        window.location.href = url.toString();
    }
}
